import viewFill from "@iconify/icons-eva/eye-fill";
import stopFill from "@iconify/icons-eva/stop-circle-fill";
import { ActionsMenu } from "src/components/Actions/ActionsMenu";
import { useCallback, useMemo } from "react";
import { useMutate } from "src/hooks/useFetch.hook";
import {
  remoteOperationTaskUrl,
  remoteOperationUrl,
} from "src/react-query/endPoints";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { tabsCurrentlyOpenedForSlice } from "src/store/tabsOpened/tabsOpened.slice";
import { fTransactionYearFirstDateTime } from "src/utils/formatTime";

export const TransactionActionMenu = ({ id, row }) => {
  // All hooks must be called unconditionally at the top level
  const { mutate, reset } = useMutate("chargers", {}, false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const onOperationSent = useCallback(
    (data) => {
      reset();
      mutate(
        {
          url: remoteOperationTaskUrl(row.chargepoint_id, data?.data?.taskId),
          method: "GET",
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries("chargers");
          },
          onSettled: () => reset(),
        }
      );
    },
    [mutate, reset, row.chargepoint_id, queryClient]
  );

  const stopTransaction = useCallback(() => {
    mutate(
      {
        url: remoteOperationUrl,
        method: "POST",
        body: {
          parameters: { transactionId: id },
          chargerId: row.chargepoint_id,
          remoteOperationType: "RemoteStop",
        },
      },
      {
        onSuccess: onOperationSent,
      }
    );
  }, [mutate, id, row.chargepoint_id, onOperationSent]);

  const viewLogs = useCallback(() => {
    // Use our timezone utility function to handle conversion properly
    // This function handles both Date objects and string timestamps correctly
    const localStartTime = fTransactionYearFirstDateTime(row.starttime || row.start_time);
    // Add 1 minute to stop time
    const stopTimeDate = row.stoptime || row.stop_time;
    const stopTimeWithOneMinute = stopTimeDate 
      ? new Date(new Date(stopTimeDate).getTime() + 120000) // Add 1 minute (60000 ms)
      : null;
    const localStopTime = stopTimeWithOneMinute 
      ? fTransactionYearFirstDateTime(stopTimeWithOneMinute)
      : fTransactionYearFirstDateTime(row.stoptime || row.stop_time);
    
    const transactionData = {
      ...row,
      starttime: localStartTime,
      stoptime: localStopTime,
      charge_box_pk: row.charge_box_pk || row.chargepoint_pk,
      chargepoint_id: row.chargepoint_id,
      transactionId: row.transactionId || row.transaction_id
    };
    
    // Validate that we have the required data
    if (!transactionData.starttime) {
      console.error('TransactionActionMenu - Missing starttime in row data');
    }
    if (!transactionData.charge_box_pk) {
      console.error('TransactionActionMenu - Missing charge_box_pk in row data');
    }
    
    dispatch(
      tabsCurrentlyOpenedForSlice.actions.setTabsOpened({
        data: { dataForLogs: transactionData },
        tabOpenedFor: "charger",
        tabOpened: "logs",
      })
    );
    navigate(`/charger/${transactionData.charge_box_pk}`);
  }, [row, navigate, dispatch]);

  const actionList = useMemo(
    () => [
      {
        id: "view",
        title: "View",
        icon: viewFill,
        routerLink: `/transactions/${id}`,
      },
      {
        id: "stop",
        title: "Stop",
        icon: stopFill,
        action: stopTransaction,
      },
      {
        id: "viewLogs",
        title: "View  Logs",
        icon: viewFill,
        action: viewLogs,
      },
    ],
    [id, stopTransaction, viewLogs]
  );

  return <ActionsMenu menuList={actionList} />;
};
